<script setup lang="ts">
import { computed, watch, onMounted } from 'vue'
import type { SurveyQuestion } from '@/types/surveyTypes'
import { Field, ErrorMessage } from 'vee-validate'
import { useSurvey, AnswerTarget } from '@/composables/onlineSurvey/useSurvey'

const { setAnswer, answers } = useSurvey()

const props = defineProps<{
  question: SurveyQuestion
  answerTarget?: AnswerTarget
}>()

const fieldName = computed(() => props.question.id)

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const numValue = parseFloat(target.value)
  const value = isNaN(numValue) ? null : numValue
  setAnswer(props.question.id, props.question.label, value, props.answerTarget)
}

const initializeValue = () => {
  const existingAnswer = answers.value.find((a) => a.id === props.question.id)
  if (existingAnswer !== undefined) {
    return existingAnswer.answer
  }
  return undefined
}

onMounted(() => {
  initializeValue()
})

watch(
  () => answers.value.find((a) => a.id === props.question.id),
  () => {
    initializeValue()
  },
)
</script>
<template>
  <div class="number-input-question">
    <div class="mb-1">
      <label :for="props.question.id" class="is-size-7">
        {{ props.question.label }}
        <span v-if="props.question.required" class="has-text-danger">*</span>
      </label>
    </div>
    <div class="control" :data-unit="props.question.unit">
      <Field :name="fieldName" :value="initializeValue()" v-slot="{ field, errorMessage }">
        <input
          :id="props.question.id"
          v-bind="field"
          type="number"
          class="input"
          :class="{
            'has-unit': props.question.unit,
          }"
          :placeholder="props.question.placeholder || ''"
          :min="props.question.min"
          :max="props.question.max"
          :step="props.question.step"
          @input="handleInput"
        />
      </Field>
    </div>
    <ErrorMessage :name="fieldName" class="help is-danger" />
  </div>
</template>

<style scoped>
@import '../../../assets/main.css';

/* Unit mit CSS ::after */
.control[data-unit]::after {
  content: attr(data-unit);
  position: absolute;
  right: 1.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #7a7a7a;
  font-size: 0.875rem;
  pointer-events: none;
  z-index: 2;
}

.control[data-unit] {
  position: relative;
}

.input.has-unit {
  padding-right: 0.5rem;
}
</style>
